import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { es } from '@/i18n/es';
import { routes } from '@/lib/routes';
import { useProfile, useSessionStatus } from '@/store/sessionStore';
import { Avatar } from '@/components/ui/Avatar';
import { ButtonLink } from '@/components/ui/Button';
import { Wordmark } from '@/components/ui/Wordmark';
import { ContentTypeToggle } from './ContentTypeToggle';
import { ThemeToggle } from './ThemeToggle';

export function Header() {
  const navigate = useNavigate();
  const profile = useProfile();
  const status = useSessionStatus();
  const [query, setQuery] = useState('');

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    navigate(`${routes.search}?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-paper/90 backdrop-blur">
      <div className="mx-auto flex h-[var(--header-height)] max-w-shell items-center gap-3 px-4">
        <Link to={routes.home} className="shrink-0" aria-label={es.app.name}>
          <Wordmark className="h-9 sm:h-11" />
        </Link>

        <ContentTypeToggle className="hidden sm:inline-flex" />

        <form onSubmit={onSubmit} role="search" className="ml-auto min-w-0 flex-1 sm:max-w-sm">
          <label htmlFor="global-search" className="sr-only">
            {es.search.label}
          </label>
          <div className="relative">
            <SearchIcon />
            <input
              id="global-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={es.search.placeholder}
              className="w-full rounded-pill border border-line bg-surface py-1.5 pl-9 pr-3 text-sm text-ink placeholder:text-muted focus:border-accent"
            />
          </div>
        </form>

        <ThemeToggle className="hidden sm:inline-flex" />

        {status === 'ready' && profile ? (
          <Link
            to={routes.profile(profile.handle)}
            className="shrink-0 rounded-pill"
            aria-label={es.nav.profile}
          >
            <Avatar name={profile.displayName} photoURL={profile.photoURL} size="sm" />
          </Link>
        ) : status === 'anon' ? (
          <ButtonLink to={routes.signIn} variant="secondary" size="sm" className="shrink-0">
            {es.nav.signIn}
          </ButtonLink>
        ) : null}
      </div>

      {/*
        Segunda fila en mobile. El botón de tema vive acá porque arriba no
        entra: el logotipo, el buscador y el avatar ya ocupan todo el ancho.
        Estaba oculto con `hidden sm:inline-flex` y eso dejaba a quien entra
        desde el celular sin ninguna forma de cambiar a modo claro.
      */}
      <div className="flex items-center gap-2 border-t border-line px-4 py-2 sm:hidden">
        <ContentTypeToggle size="compact" className="flex-1 justify-center" />
        <ThemeToggle className="shrink-0" />
      </div>
    </header>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path d="m13.5 13.5 3.5 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}
